<template>
    <Select
        class="component--select_search"
        :name="name"
        :placeholder="placeholder"
        :empty="empty"
        :multiple="multiple"
        :items="searchedItems"
        :active="active"
        :activeLabel="_activeLabel"
        :unfocusByHandler="false"
        v-on:dropdown-focus="changeFocus"
        v-on:selectChange="selectChange"
        v-on:select="value => $emit('select', value)"
    >
        <template v-slot:title>
            <!-- Default title slot with click event -->
            <div
                v-if="!isShowInput"
                @click="onClickTitle"
                class="placeholder placeholder_inline placeholder_icon">
                <Icon id="arrow-down" v-if="!isDisabled"></Icon>

                {{ _activeLabel || placeholder }}
            </div>
            <div
                v-else
                style="--icons-offset: 50px"
                class="placeholder placeholder_input placeholder_icon"
                v-click-outside="onClickOutside">
                <input
                    type="text"
                    v-focus
                    :value="_searchText"
                    @input="e => setSearch(e.target.value)"
                    @keydown.enter="$emit('enter')"
                >

                <Icon v-if="_searchText" id="delete" color="purple" @click="resetSearch" />
                <Icon id="lens"/>
            </div>
        </template>

        <template v-slot:actions>
            <slot name="actions"></slot>
        </template>

        <template v-slot:empty>
            <slot name="empty"></slot>
        </template>
    </Select>
</template>

<script>
import selectMixin from "@common/vue/components/form/selectMixin";
import {objectFilter} from "@common/utils/object";

/**
 * Этот компонент позволяет:
 * - Показать/скрыть выпадающий список
 * - Поиск элементов из списка
 * - Выбор элемента
 * - Событие расфокусировки после активности
 */
export default {
    name: "SelectSearch",
    mixins: [selectMixin],
    props: {
        /** Если передано это свойство, оно используется извне вместо searchStateText */
        searchText: String,
        unFocusClearValue: {
          type: Boolean,
          default: true,
        }
    },
    data() {
        return {
            /** Показывать поле ввода или имитировать его дизайн */
            isShowInput: false,
            /** Эта переменная используется внутри компонента для отслеживания состояния строки поиска */
            searchStateText: '',
        }
    },
    computed: {
        isProp() {
            return this.searchText !== undefined;
        },
        _searchText() {
            return this.isProp ? this.searchText : this.searchStateText;
        },
        lowerSearchText() {
            return this._searchText.trim().toLowerCase();
        },
        searchedItems() {
            if (this._searchText) {
                return (this.items[0] || {}).hasOwnProperty("id") ?
                    Object.values(this.items).filter(this.isMatched) :
                    objectFilter(this.items, ([a, item]) => this.isMatched(item));
            }

            return this.items;
        },
        labelProperty() {
            return this.items[0]?.hasOwnProperty('label') ? 'label' : 'name';
        },
    },
    methods: {
        onClickTitle() {
            this.isShowInput = !this.isShowInput;
        },
        onClickOutside(e) {
            // Event target is required.
            if (!e.target) return false;

            // Click outside after active input component.
            if (this.isShowInput && !e.target.closest('.component--select_search')) {
                this.$emit('blur', {event: e});
            }

            if (!this._searchText.length) {
                this.isShowInput = false;
            }
        },
        setSearch(value) {
            this.$emit('changeSearchText', value);

            if (!this.isProp) {
                this.searchStateText = value;
            }
        },
        resetSearch() {
            this.setSearch('');
            this.resetDropdown();
        },
        changeFocus(focus) {
            this.$emit('dropdown-focus', focus);

            if (this.unFocusClearValue && !focus) {
                this.setSearch('');
            }
        },
        selectChange(v) {
            this.$emit('selectChange', v);
            this.setSearch('');
            this.isShowInput = false;
            this.closeDropdown();
        },
        isMatched(item) {
            return item[this.labelProperty]
                .toLowerCase()
                .includes(this.lowerSearchText);
        },
        resetDropdown() {
            this.isShowInput = false;
            this.closeDropdown();
        },
        closeDropdown() {
            this.$children[0].closeDropdown();
        },
    }
}
</script>

<style lang="scss">
.component--select {
    .simplebar-vertical {
        right: -8px;
    }
}
</style>
